/* eslint-disable @typescript-eslint/no-explicit-any */
import { Card, CardContent } from "@/components/ui/card";
import { ListPageTopBar } from "@/components/layout/dashboard-layout";
import { useTable } from "@/hooks/useTable";
import { useNavigate, useParams } from "react-router-dom";
import { useEmailStats } from "./use-email-stats";
import { Button } from "@/components/ui/button";
import { ArrowLeftIcon, Columns3Icon } from "lucide-react";
import { TableColumnsHandle } from "@/components/shared/table-columns-handle";
import { Fragment } from "react/jsx-runtime";
import { useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { RecordNotFound } from "@/components/shared/record-not-found";
import ErrorFallback from "@/components/shared/error-fallback";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ReactTableSkelton } from "@/pages/email-read-logs/utils";
import { cn } from "@/lib/utils";
import { ResizeBox } from "@/components/shared/resize-box";
import { EmailStatsTableCell } from "./email-stats-table-cell";

export const EmailStats = () => {
  const { log_id } = useParams<{ log_id: string }>();
  const navigate = useNavigate();
  const { emailStats, loading } = useEmailStats(log_id);

  const [tableColumn, setTableColumn] = useState([]);

  const { tableHeader, table, tableKeys, onResize, onSorting } = useTable({
    data: emailStats,
    view: [
      "sender_email",
      "subject",
      "received_at",
      "read_time",
      "total_attachments",
      "read_attachments",
      "ignored_attachments",
      "body_content",
      "status",
      "ignored_reasons",
    ],
    verifyKey: "email_stats",
    visible: tableColumn,
    columnSize: {
      sender_email: 227,
      subject: 135,
      received_at: 158,
      read_time: 158,
      total_attachments: 101,
      read_attachments: 101,
      ignored_attachments: 101,
      body_content: 101,
      ignored_reasons: 101,
      status: 80,
    },
  });

  return (
    <Fragment>
      <div className="flex flex-col gap-5">
        <ListPageTopBar title="Email Stats">
          <TableColumnsHandle
            tableKeys={tableKeys}
            tableColumn={tableColumn}
            setTableColumn={setTableColumn}
            onSorting={onSorting}
            tableKey="email_stats"
            labels={{
              sender_email: "Sender Email",
              subject: "Subject",
              received_at: "Received At",
              read_time: "Read Time",
              total_attachments: "Total Attachments",
              read_attachments: "Read Attachments",
              ignored_attachments: "Ignored Attachments",
              body_content: "Body Content",
              status: "Status",
              ignored_reasons: "Ignored Reasons",
            }}
          >
            <Columns3Icon /> Column
          </TableColumnsHandle>
          <Button
            variant={"action"}
            onClick={() => {
              sessionStorage.setItem("email_read_logs_return_from_stats", "1");
              navigate(-1);
            }}
          >
            <ArrowLeftIcon /> Back
          </Button>
        </ListPageTopBar>

        {loading ? (
          <ReactTableSkelton />
        ) : (
          <Card className="overflow-hidden">
            <CardContent className="max-w-[100%] p-0 overflow-auto scrollbar h-auto min-h-[350px] md:h-[calc(100dvh-340px)]">
              <ErrorBoundary
                fallbackRender={({ error, resetErrorBoundary }) => (
                  <ErrorFallback
                    error={error}
                    errorInfo={error.stack || ""}
                    resetErrorBoundary={resetErrorBoundary}
                  />
                )}
              >
                <Table style={{ borderCollapse: "collapse", width: "100%" }}>
                  <TableHeader>
                    <TableRow>
                      {tableHeader?.map((header: any, index: number) => (
                        <TableHead
                          key={header.accessorKey}
                          style={{
                            width: header.width,
                            minWidth: header.minWidth,
                            maxWidth: header.maxWidth,
                          }}
                          className={cn({ "pl-5": index === 0 })}
                        >
                          <ResizeBox
                            width={header.width}
                            onResize={(props: any) =>
                              onResize({ size: props, key: header.accessorKey })
                            }
                          >
                            <span>{header.header}</span>
                          </ResizeBox>
                        </TableHead>
                      ))}
                      <TableHead className="w-auto" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {table.map((row: any, rowIndex: number) => (
                      <TableRow key={rowIndex}>
                        {row.data.map((cell: any, index: number) => (
                          <TableCell
                            key={cell.key + index}
                            width={cell.width}
                            style={{ width: cell.width }}
                            className={cn({ "pl-3": index === 0 })}
                          >
                            <EmailStatsTableCell
                              header={cell.key}
                              value={cell.value}
                            />
                          </TableCell>
                        ))}
                        <TableCell className="w-auto" />
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {table.length === 0 ? <RecordNotFound /> : null}
              </ErrorBoundary>
            </CardContent>
          </Card>
        )}
      </div>
    </Fragment>
  );
};
